import { Dispatch } from 'react-redux';
import { AxiosError } from "axios";
import { AuthActionCreators, IApplicationState, IRccapLookupDataResponse } from "../";
import { IRccap, IListItem, IRccapRootCause, IBaseResponse, IRccapCenterResponseAdditionalQuestionAnswer, ICenterBase, IContact, IRccapRequest, IRccapReportDetail, ISwapFailureRateReportRow } from "@nkr/models/index";
import { IRpSwapFailureRate } from '@nkr/models/center-performance/relative-performance';
import { RccapStatus } from '@nkr/enums/index';
import { IPost } from '../authorization';

export interface ILookupData {
    phases: IListItem[],
    billable: IListItem[],
    avoidable: IListItem[],
    causes: IRccapRootCause[],
    correctiveActions: IListItem[],
    satisfactoryResponses: IListItem[],
    rccapStatusList: IListItem[],
    staffList: IContact[],
    centers: ICenterBase[]
}

export const ActionTypes = {
    SET_RCCAP_LIST: "SET_RCCAP_LIST",
    SET_RCCAP_LOOKUP_DATA: "SET_RCCAP_LOOKUP_DATA",
    SET_RCCAP_INFO: "SET_RCCAP_INFO",
    SET_RCCAP_STATUS_LIST: "SET_RCCAP_STATUS_LIST"
}

export interface IRccapListResponse {
    data: {
        success: boolean,
        message: string,
        results: IRccap[],
        swapFailureRate?: IRpSwapFailureRate
    }
}

export interface IRccapResponse {
    data: {
        success: boolean,
        message: string,
        results: IRccap
    }
}

export interface IRccapAdminResponse {
    data: {
        success: boolean,
        message: string,
        results: number
    }
}

export interface IRccapStatusListResponse {
    data: {
        success: boolean,
        message: string,
        results: IListItem[]
    }
}

export interface IRccapReportDetailResponse {
    data: {
        success: boolean,
        message: string,
        results: IRccapReportDetail[]
    }
}

export interface ISwapFailureRateReportResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            data: ISwapFailureRateReportRow[],
            swapFailureRate: IRpSwapFailureRate
        }
    }
}

export const ActionCreators = {
    getRccapStatusList: () => (dispatch: Dispatch<IApplicationState>): Promise<IRccapStatusListResponse> => {
        var options = {
            url: __API__ + `/rccap/status-list`
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IRccapStatusListResponse) => {
                if (response.data.success) {
                    dispatch(ActionCreators.setRccapStatusList(response.data.results));
                }

                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    setRccapStatusList: (rccapStatusList: IListItem[]) => {
        return {
            type: ActionTypes.SET_RCCAP_STATUS_LIST,
            rccapStatusList
        };
    },
    getRccapLookupData: (save: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<IRccapLookupDataResponse> => {
        var options = {
            url: __API__ + `/rccap/lookup-data`
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IRccapLookupDataResponse) => {
                if (response.data.success) {
                    if (save)
                        dispatch(ActionCreators.setRccapLookupData(response.data.results));
                }

                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    setRccapLookupData: (lookupData: ILookupData) => {
        return {
            type: ActionTypes.SET_RCCAP_LOOKUP_DATA,
            lookupData
        };
    },
    getRccapList: (centerId: number, status: RccapStatus) => (dispatch: Dispatch<IApplicationState>): Promise<IRccapListResponse> => {
        var options = {
            url: __API__ + `/rccap?centerId=${centerId}&status=${status}`
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IRccapListResponse) => {
                if (response.data.success)
                    dispatch(ActionCreators.setRccapList(response.data.results));

                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    setRccapList: (list: IRccap[]) => {
        return {
            type: ActionTypes.SET_RCCAP_LIST,
            list
        };
    },
    getRccap: (id: number, save: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<IRccapResponse> => {
        var options = {
            url: __API__ + `/rccap/${id}`
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IRccapResponse) => {
                if (response.data.success) {
                    if (save)
                        dispatch(ActionCreators.setRccap(response.data.results));
                }

                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    setRccap: (rccap: IRccap) => {
        return {
            type: ActionTypes.SET_RCCAP_INFO,
            rccap
        };
    },
    saveRccap: (request: IRccapRequest) => (dispatch: Dispatch<IApplicationState>): Promise<IRccapAdminResponse> => {
        var options: IPost = {
            url: __API__ + '/rccap',
            data: request
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IRccapAdminResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    saveRccapCenterResponse: (issueDescription: string, questions: IRccapCenterResponseAdditionalQuestionAnswer[], rootCausePrimary: number, rootCauseSecondary: number, correctiveAction: string, responseDate: string, responder: string) => (dispatch: Dispatch<IApplicationState>, getState: () => IApplicationState) => {
        let { id } = getState().rccap.info;

        var options: IPost = {
            url: __API__ + '/rccap/center-response',
            data: {
                id,
                issueDescription,
                questions,
                rootCausePrimary,
                rootCauseSecondary,
                correctiveAction,
                responseDate,
                responder
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    deleteRccap: (id: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/rccap/${id}`
        };

        try {
            return await dispatch(AuthActionCreators.apiDelete(options));
        } catch (error) {
            throw error;
        }
    },
    getRccapReportDetail: (report: number, startDate: string, endDate: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IRccapReportDetailResponse> => {
        let options = {
            url: `${__API__}/rccap/report/detail?report=${report}&startDate=${startDate}&endDate=${endDate}`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
    getSwapFailureRateReport: (centerId: number, startDate: string, endDate: string) => async (dispatch: Dispatch<IApplicationState>): Promise<ISwapFailureRateReportResponse> => {
        let options = {
            url: `${__API__}/rccap/swap-failure-report?centerId=${centerId}&startDate=${startDate}&endDate=${endDate}`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
    getRccapReportDetailPdf: (report: number, startDate: string, endDate: string, rccapId: number): any => (dispatch: Dispatch<IApplicationState>) => {
        var options = {
            url: __API__ + `/rccap/report/detail/pdf`,
            responseType: 'blob',
            data: {
                report,
                startDate,
                endDate,
                rccapId
            }
        };

        return dispatch(AuthActionCreators.apiPost(options));
    },
}

export interface IRccapState {
    list: IRccap[],
    lookupData: ILookupData,
    info: IRccap
}

var initialState: IRccapState = {
    list: [],
    lookupData: {
        phases: [],
        billable: [],
        avoidable: [],
        causes: [],
        correctiveActions: [],
        satisfactoryResponses: [],
        rccapStatusList: [],
        staffList: [],
        centers: []
    },
    info: null
};

interface ISetRccapList {
    type: string,
    list: IRccap[]
}

interface ISetRccapLookupData {
    type: string,
    lookupData: ILookupData
}

interface ISetRccap {
    type: string,
    rccap: IRccap
}

interface ISetRccapStatusList {
    type: string,
    rccapStatusList: IListItem[]
}

const setRccapList = (state: IRccapState, action: ISetRccapList) => {
    state.list = action.list;
    return state;
}

const setRccapLookupData = (state: IRccapState, action: ISetRccapLookupData) => {
    state.lookupData = action.lookupData;
    return state;
}

const setRccap = (state: IRccapState, action: ISetRccap) => {
    state.info = action.rccap;
    return state;
}

const setRccapStatusList = (state: IRccapState, action: ISetRccapStatusList) => {
    state.lookupData = { ...state.lookupData, rccapStatusList: action.rccapStatusList }
    return state;
}

const createReducer = (initialState: IRccapState, handlers: any) => {
    return function reducer(state = initialState, action: any) {
        if (handlers.hasOwnProperty(action.type)) {
            let s = Object.assign({}, state);
            return handlers[action.type](s, action);
        } else {
            return state;
        }
    }
}

const handlers = {
    [ActionTypes.SET_RCCAP_LIST]: setRccapList,
    [ActionTypes.SET_RCCAP_LOOKUP_DATA]: setRccapLookupData,
    [ActionTypes.SET_RCCAP_INFO]: setRccap,
    [ActionTypes.SET_RCCAP_STATUS_LIST]: setRccapStatusList
};

export const Reducer = createReducer(initialState, handlers);
